import {
  Button,
  Checkbox,
  DateTimePicker,
  EmptyState,
  PageHeader,
  Skeleton,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { ResultsDashboard } from '@/features/polls/ResultsDashboard';
import { ViewersModal } from '@/features/polls/ViewersModal';
import { useClosePoll, useDeletePoll, usePollResults, useUpdatePoll } from '@/features/polls/hooks';
import { ApiClientError } from '@/lib/api';
import { A, useNavigate, useParams } from '@solidjs/router';
import { For, Show, createSignal } from 'solid-js';

import { localDateTime, validDateTime } from '@/components/DateTimePicker';

export default function PollResults() {
  const params = useParams<{ pollId: string }>();
  const query = usePollResults(() => params.pollId);
  const { session } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const close = useClosePoll(params.pollId);
  const remove = useDeletePoll(params.pollId);
  const update = useUpdatePoll(params.pollId);
  const [viewersOpen, setViewersOpen] = createSignal(false);
  const [editingDeadline, setEditingDeadline] = createSignal(false);
  const [deadline, setDeadline] = createSignal('');
  const [reopen, setReopen] = createSignal(false);

  return (
    <section>
      <PageHeader
        title={query.data?.data.poll.title ?? 'Poll results'}
        actions={
          <A href={`/polls/${params.pollId}`}>
            <Button variant="secondary">Back to poll</Button>
          </A>
        }
      />
      <div class="mx-auto max-w-6xl p-6">
        <Show when={!query.isPending} fallback={<Skeleton class="h-64" />}>
          <Show
            when={query.data?.data}
            fallback={<EmptyState variant="error" title="Results are unavailable" />}
          >
            {(results) => {
              const poll = () => results().poll;
              const canDelete = () =>
                poll().canManage || (session()?.permissions.has('polls:delete') ?? false);
              const canClose = () =>
                (session()?.permissions.has('polls:close') ?? false) && canDelete();
              return (
                <ResultsDashboard
                  results={results()}
                  actions={
                    <>
                      <Show when={poll().canManage}>
                        <Button variant="secondary" onClick={() => setViewersOpen(true)}>
                          Manage viewers
                        </Button>
                        <Show
                          when={editingDeadline()}
                          fallback={
                            <Button
                              variant="secondary"
                              onClick={() => {
                                setDeadline(localDateTime(poll().closesAt));
                                setReopen(false);
                                setEditingDeadline(true);
                              }}
                            >
                              Change deadline
                            </Button>
                          }
                        >
                          <DateTimePicker
                            label="Closes at"
                            value={deadline()}
                            onChange={setDeadline}
                            disabled={update.isPending}
                          />
                          <Show when={poll().status === 'closed'}>
                            <Checkbox
                              label="เปิดรับคำตอบอีกครั้ง"
                              checked={reopen()}
                              onChange={setReopen}
                              disabled={update.isPending}
                            />
                          </Show>
                          <Button
                            size="sm"
                            loading={update.isPending}
                            disabled={!validDateTime(deadline())}
                            onClick={async () => {
                              if (
                                reopen() &&
                                deadline() &&
                                new Date(deadline()).getTime() <= Date.now()
                              ) {
                                toast.error(
                                  'Choose a future deadline or no deadline to reopen the poll',
                                );
                                return;
                              }
                              try {
                                await update.mutateAsync({
                                  closesAt:
                                    deadline() === localDateTime(poll().closesAt)
                                      ? poll().closesAt
                                      : deadline()
                                        ? new Date(deadline()).toISOString()
                                        : null,
                                  ...(reopen() ? { reopen: true } : {}),
                                });
                                setEditingDeadline(false);
                                toast.success(reopen() ? 'Poll reopened' : 'Deadline updated');
                              } catch (error) {
                                toast.error(
                                  error instanceof ApiClientError
                                    ? error.error.message
                                    : 'Could not update deadline',
                                );
                              }
                            }}
                          >
                            Save deadline
                          </Button>
                        </Show>
                      </Show>
                      <Show when={poll().status === 'open' && canClose()}>
                        <Button
                          variant="secondary"
                          loading={close.isPending}
                          onClick={async () => {
                            if (
                              !(await confirm({
                                title: `Close ${poll().title}?`,
                                confirmLabel: 'Close',
                              }))
                            )
                              return;
                            await close.mutateAsync(undefined as never);
                            toast.success('Poll closed');
                          }}
                        >
                          Close now
                        </Button>
                      </Show>
                      <Show when={canDelete()}>
                        <Button
                          variant="danger"
                          loading={remove.isPending}
                          onClick={async () => {
                            if (
                              !(await confirm({
                                title: `Delete ${poll().title}?`,
                                body: 'This permanently deletes every response.',
                                confirmLabel: 'Delete',
                                danger: true,
                              }))
                            )
                              return;
                            await remove.mutateAsync(undefined as never);
                            navigate('/polls');
                          }}
                        >
                          Delete poll
                        </Button>
                      </Show>
                    </>
                  }
                />
              );
            }}
          </Show>
        </Show>
      </div>
      <Show when={viewersOpen()}>
        <ViewersModal open pollId={params.pollId} onClose={() => setViewersOpen(false)} />
      </Show>
    </section>
  );
}
